import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/**
 * Collapsible training-zones explanation (mirrors iOS ExerciseDetailsDialogVC info card).
 * Formulas match zoneUtils / HeartRateIntensityCalculator (Tanaka male, Gulati female).
 */
export function TrainingZonesInfoPanel({ mhr = null, age = null, gender = null }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const sexLabel =
    String(gender || "").toLowerCase() === "female"
      ? t("gender_female", "Female")
      : t("gender_male", "Male");

  return (
    <div className="player-exercise-details__zones-info">
      <div className="player-exercise-details__zones-title-row">
        <h3 className="player-exercise-details__section-title player-exercise-details__section-title--inline">
          {t("training_zones", "Training Zones")}
        </h3>
        <button
          type="button"
          className={`player-exercise-details__zones-info-btn${
            open ? " player-exercise-details__zones-info-btn--open" : ""
          }`}
          aria-expanded={open}
          aria-controls="training-zones-info-panel"
          aria-label={t(
            "training_zones_info_aria",
            "Explain training zones, intensity, and MHR formula"
          )}
          onClick={() => setOpen((v) => !v)}
        >
          <InfoOutlinedIcon style={{ fontSize: 18 }} />
        </button>
      </div>

      {open ? (
        <div
          id="training-zones-info-panel"
          className="player-exercise-details__zones-info-card"
          role="region"
          aria-label={t(
            "training_zones_info_aria",
            "Explain training zones, intensity, and MHR formula"
          )}
        >
          <h4 className="player-exercise-details__zones-info-heading">
            {t(
              "training_zones_mhr_title",
              "Max Heart Rate Estimation (Tanaka / Gulati)"
            )}
          </h4>
          <p className="player-exercise-details__zones-info-text">
            {t(
              "training_zones_mhr_body",
              "Estimates physiological MHR bounds:\n• Men: 208 − (0.7 × Age)\n• Women: 206 − (0.88 × Age)"
            )}
          </p>
          {mhr != null && Number(mhr) > 0 ? (
            <p className="player-exercise-details__zones-info-meta">
              {t(
                "training_zones_mhr_applied",
                "Applied MHR: {{mhr}} bpm (age {{age}}, {{sex}})",
                {
                  mhr: Math.round(mhr),
                  age: age != null ? age : "—",
                  sex: sexLabel,
                }
              )}
            </p>
          ) : null}

          <h4 className="player-exercise-details__zones-info-heading">
            {t("training_zones_intensity_title", "Intensity")}
          </h4>
          <p className="player-exercise-details__zones-info-text">
            {t(
              "training_zones_intensity_body",
              "Intensity is heart rate as a percentage of estimated max heart rate (MHR).\n• Max Intensity: peak HR ÷ MHR × 100\n• Avg Intensity: average HR ÷ MHR × 100"
            )}
          </p>

          <h4 className="player-exercise-details__zones-info-heading">
            {t(
              "training_zones_thresholds_title",
              "Physiological Training Thresholds"
            )}
          </h4>
          <p className="player-exercise-details__zones-info-text">
            {t(
              "training_zones_thresholds_body",
              "• Peak (90%+): Maximum output, anaerobic glycolysis.\n• Cardio (80–90%): Aerobic capacity expansion & high stamina.\n• Aerobic (70–80%): Aerobic base, muscle glycogen efficiency.\n• Fat Burn (60–70%): Submaximal fat oxidation, vascular capillary density.\n• Very Light (50–60%): Lactic acid clearance, active recovery."
            )}
          </p>
        </div>
      ) : null}
    </div>
  );
}

export default TrainingZonesInfoPanel;
